/**
 * Hook: manages the lifecycle of BufferGeometry objects parsed from
 * compiled OpenSCAD output (STL blobs).
 *
 * Responsibilities:
 * 1. Parse incoming Blob / ColoredOutput arrays into BufferGeometry objects.
 * 2. Support incremental (streaming) updates — when a single new color is
 *    appended within the same compile run, parse only that item and append it
 *    to the rendered geometries without touching the already-rendered ones.
 * 3. Cancel in-flight parsing when a new compile run starts.
 * 4. Dispose previous geometries when they are replaced, freeing GPU memory.
 * 5. Dispose all geometries on unmount.
 *
 * Architecture:
 *   compileId changes  → full reset: cancel in-flight parses, dispose old
 *                        geometries, start fresh.
 *   compileId the same → append: parse only newly arrived items (by index).
 *   coloredOutputs empty → single-color fallback: parse the raw output blob.
 */

import { useEffect, useRef, useState } from 'react';
import { BufferGeometry } from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import type { ColoredOutput } from '@/hooks/useOpenSCAD';
import type { ColoredGeometry } from '@/components/viewer/types';
import { disposeGeometry } from '@/components/viewer/materials/materialUtils';
import { generateBoxProjectionUVs } from '@/utils/uvUtils';

interface GeometryState {
  geometry: BufferGeometry | null;
  coloredGeometries: ColoredGeometry[];
}

export function useGeometryLifecycle(
  output: Blob | undefined,
  coloredOutputs: ColoredOutput[],
): GeometryState {
  // Derive compileId from the first item — it's embedded in every ColoredOutput,
  // so it's always in sync with coloredOutputs (same React state update, same render).
  const compileId =
    coloredOutputs.length > 0 ? coloredOutputs[0].compileId : null;
  const [state, setState] = useState<GeometryState>({
    geometry: null,
    coloredGeometries: [],
  });

  /**
   * Monotonic counter. Incremented every time a new compile run begins (or the
   * single-color path fires). Any async parse that captures a generation value
   * at launch will bail out if the counter has moved on by the time it resolves.
   */
  const parseGeneration = useRef(0);

  /**
   * All BufferGeometry objects allocated for the current generation.
   * Indexed by position in coloredOutputs so we can assemble them in order.
   */
  const currentGeometriesRef = useRef<(ColoredGeometry | null)[]>([]);

  /**
   * Number of items from the current compileId that have already been queued
   * for parsing (to avoid re-queuing on each render).
   */
  const parsedCountRef = useRef(0);

  /** Previous compileId, to detect when a new compile starts. */
  const prevCompileIdRef = useRef<string | null>(null);

  /** All geometries allocated (across all items) for disposal tracking. */
  const allAllocatedGeoms = useRef<BufferGeometry[]>([]);

  const disposeAll = () => {
    for (const geom of allAllocatedGeoms.current) {
      disposeGeometry(geom);
    }
    allAllocatedGeoms.current = [];
    currentGeometriesRef.current = [];
  };

  // --- Multi-color streaming path ---
  useEffect(() => {
    if (!coloredOutputs || coloredOutputs.length === 0) return;

    const isNewCompile = compileId !== prevCompileIdRef.current;

    if (isNewCompile) {
      // New compile: cancel all in-flight parses from the previous run,
      // dispose previous geometries, and reset tracking state.
      const thisGeneration = ++parseGeneration.current;
      prevCompileIdRef.current = compileId;
      parsedCountRef.current = 0;
      disposeAll();
      // Clear rendered state immediately so old geometry disappears
      setState({ geometry: null, coloredGeometries: [] });

      // Kick off parsing for however many items are already available
      const startIndex = 0;
      const itemsToQueue = coloredOutputs.slice(startIndex);
      parsedCountRef.current = itemsToQueue.length;
      currentGeometriesRef.current = new Array(itemsToQueue.length).fill(null);

      itemsToQueue.forEach((coloredOutput, idx) => {
        coloredOutput.blob.arrayBuffer().then((buffer) => {
          if (parseGeneration.current !== thisGeneration) return;
          const loader = new STLLoader();
          const geom = loader.parse(buffer);
          geom.computeVertexNormals();
          // STL has no UVs — generate box-projection UVs for texture mapping
          generateBoxProjectionUVs(geom);
          allAllocatedGeoms.current.push(geom);
          currentGeometriesRef.current[idx] = {
            geometry: geom,
            color: coloredOutput.color,
            isGhost: coloredOutput.isGhost,
            excludedFromExport: coloredOutput.excludedFromExport,
            material: coloredOutput.material,
            textureScripts: coloredOutput.textureScripts,
            interactive: coloredOutput.interactive,
            annotation: coloredOutput.annotation,
            motion: coloredOutput.motion,
            cloneKey: coloredOutput.cloneKey,
            shaderScript: coloredOutput.shaderScript,
          };
          // Render what we have so far (in original order, skipping nulls)
          const ready = currentGeometriesRef.current.filter(
            (g): g is ColoredGeometry => g !== null,
          );
          setState({ geometry: null, coloredGeometries: ready });
        });
      });
    } else {
      // Same compile run — queue parsing for any newly appended items only
      const alreadyQueued = parsedCountRef.current;
      const newItems = coloredOutputs.slice(alreadyQueued);
      if (newItems.length === 0) return;

      const thisGeneration = parseGeneration.current;
      const baseIndex = alreadyQueued;

      // Grow the slots array for new items
      currentGeometriesRef.current = [
        ...currentGeometriesRef.current,
        ...new Array(newItems.length).fill(null),
      ];
      parsedCountRef.current = coloredOutputs.length;

      newItems.forEach((coloredOutput, i) => {
        const idx = baseIndex + i;
        coloredOutput.blob.arrayBuffer().then((buffer) => {
          if (parseGeneration.current !== thisGeneration) return;
          const loader = new STLLoader();
          const geom = loader.parse(buffer);
          geom.computeVertexNormals();
          // STL has no UVs — generate box-projection UVs for texture mapping
          generateBoxProjectionUVs(geom);
          allAllocatedGeoms.current.push(geom);
          currentGeometriesRef.current[idx] = {
            geometry: geom,
            color: coloredOutput.color,
            isGhost: coloredOutput.isGhost,
            excludedFromExport: coloredOutput.excludedFromExport,
            material: coloredOutput.material,
            textureScripts: coloredOutput.textureScripts,
            interactive: coloredOutput.interactive,
            annotation: coloredOutput.annotation,
            motion: coloredOutput.motion,
            cloneKey: coloredOutput.cloneKey,
            shaderScript: coloredOutput.shaderScript,
          };
          const ready = currentGeometriesRef.current.filter(
            (g): g is ColoredGeometry => g !== null,
          );
          setState({ geometry: null, coloredGeometries: ready });
        });
      });
    }
  }, [coloredOutputs, compileId]);

  // --- Single-color fallback path (no coloredOutputs) ---
  useEffect(() => {
    if (!output || !(output instanceof Blob) || coloredOutputs.length > 0)
      return;

    const thisGeneration = ++parseGeneration.current;
    prevCompileIdRef.current = null;
    parsedCountRef.current = 0;
    disposeAll();

    output.arrayBuffer().then((buffer) => {
      if (parseGeneration.current !== thisGeneration) return;
      const loader = new STLLoader();
      const geom = loader.parse(buffer);
      geom.computeVertexNormals();
      // STL has no UVs — generate box-projection UVs for texture mapping
      generateBoxProjectionUVs(geom);
      allAllocatedGeoms.current.push(geom);
      setState({ geometry: geom, coloredGeometries: [] });
    });
  }, [output, coloredOutputs.length]);

  // Dispose all geometries on final unmount
  useEffect(() => {
    return () => {
      disposeAll();
    };
  }, []);

  return state;
}
